import api from './api';

const billingService = {
  // Invoices
  getInvoices: (params) => api.get('/invoices', { params }),
  getInvoice: (id) => api.get(`/invoices/${id}`),
  createInvoice: (data) => api.post('/invoices', data),
  sendInvoice: (id) => api.post(`/invoices/${id}/send`),
  downloadInvoice: (id) => api.get(`/invoices/${id}/download`, { responseType: 'blob' }),

  // ── E-invoicing (Phase 14d.1) ────────────────────────────────
  /**
   * Submit a B2B invoice to the Invoice Registration Portal.
   * Idempotent — re-submitting an ACTIVE row returns the existing
   * IRN without re-hitting the IRP.
   */
  submitInvoiceToIRP: (id) => api.post(`/invoices/${id}/submit-to-irp`),

  /**
   * Cancel an ACTIVE e-invoice within the 24-hour GSTN window.
   * ``reasonCode`` is one of: ``'1'`` (Duplicate), ``'2'`` (Data
   * entry mistake), ``'3'`` (Order cancelled), ``'4'`` (Others).
   * Beyond 24h the IRP refuses — issue a credit note instead.
   */
  cancelEInvoice: (id, { reasonCode, remarks = null } = {}) =>
    api.post(`/invoices/${id}/cancel-e-invoice`, {
      reason_code: reasonCode,
      remarks,
    }),

  // Payments
  getPayments: (params) => api.get('/payments', { params }),
  recordPayment: (data) => api.post('/payments', data),
  getPaymentHistory: (invoiceId) => api.get(`/invoices/${invoiceId}/payments`),

  // Pricing rate cards (pricing-service) — the api-gateway routes
  // ``/rate-cards`` to pricing-service except for ``/mine`` which
  // goes to billing-service for *commission* rate cards. The host
  // management page calls these.
  //
  // ``params.warehouse_id`` scopes the list — the page renders a
  // warehouse picker since pricing-service doesn't expose a
  // host-scoped surface (cards live under warehouses, not hosts).
  getRateCards: (params) => api.get('/rate-cards', { params }),
  getRateCard: (id) => api.get(`/rate-cards/${id}`),
  createRateCard: (data) => api.post('/rate-cards', data),
  // pricing-service is version-tracked: editing means "publish a new
  // version of this card", not in-place mutation. The old
  // ``PUT /rate-cards/{id}`` never existed there — silently 404'd
  // and the page's Edit button was always broken.
  updateRateCard: (id, data) => api.post(`/rate-cards/${id}/version`, data),
  deleteRateCard: (id) => api.delete(`/rate-cards/${id}`),

  // Payouts
  getPayouts: (params) => api.get('/payouts', { params }),
  getEscrowHolds: (params) => api.get('/escrow', { params }),
  getPayout: (id) => api.get(`/payouts/${id}`),
  approvePayouts: (ids) => api.post('/payouts/approve', { payout_ids: ids }),
  getPayoutSummary: () => api.get('/payouts/summary'),

  // GST Reports — legacy mock endpoints (pre-Phase-14c)
  getGSTReport: (params) => api.get('/billing/gst-report', { params }),
  exportGSTR1: (params) => api.get('/billing/gstr1/export', { params, responseType: 'blob' }),

  /**
   * Phase 14c — section-grouped GSTR-1 report.
   *
   * Scoped by ``(supplier_gstin, financial_year, month)``. Returns
   * a JSON body with B2B / B2CL / B2CS / CDNR / CDNUR / HSN / DOCS
   * sections plus diagnostics (``excluded_backfilled``,
   * ``excluded_draft_or_cancelled``).
   */
  getGSTR1Report: ({ supplierGstin, financialYear, month }) =>
    api.get('/reports/gstr1', {
      params: {
        supplier_gstin: supplierGstin,
        financial_year: financialYear,
        month,
      },
    }),

  /**
   * CSV form of the same report. One stream with a ``section``
   * column identifying the bucket — easier to feed into a filing
   * tool than a zip of per-section files.
   */
  downloadGSTR1CSV: ({ supplierGstin, financialYear, month }) =>
    api.get('/reports/gstr1.csv', {
      params: {
        supplier_gstin: supplierGstin,
        financial_year: financialYear,
        month,
      },
      responseType: 'blob',
    }),

  /**
   * Phase 14f.1 — Tax ledger (CGST Rule 56) summary for a
   * (supplier_gstin, FY [, month]) slice. Returns per-head
   * balances (output + debit − credit − cash) plus the raw
   * entry list for audit drill-down.
   */
  getTaxLedger: ({ supplierGstin, financialYear, month = null }) =>
    api.get('/reports/tax-ledger', {
      params: {
        supplier_gstin: supplierGstin,
        financial_year: financialYear,
        ...(month ? { month } : {}),
      },
    }),

  /**
   * Phase 14g — GSTR-3B monthly summary return. Scoped by
   * (supplier_gstin, FY, month); returns sections 3.1(a), 3.2,
   * and 6.1 pre-computed for the filing tool of the host's
   * choice.
   */
  getGSTR3BReport: ({ supplierGstin, financialYear, month }) =>
    api.get('/reports/gstr3b', {
      params: {
        supplier_gstin: supplierGstin,
        financial_year: financialYear,
        month,
      },
    }),

  downloadGSTR3BCSV: ({ supplierGstin, financialYear, month }) =>
    api.get('/reports/gstr3b.csv', {
      params: {
        supplier_gstin: supplierGstin,
        financial_year: financialYear,
        month,
      },
      responseType: 'blob',
    }),

  /**
   * Phase 14i — annual GSTR-9 summary return. Full-year rollup
   * scoped by (supplier_gstin, FY) — tables 4 + 4N + 9 + 17.
   */
  getGSTR9Report: ({ supplierGstin, financialYear }) =>
    api.get('/reports/gstr9', {
      params: {
        supplier_gstin: supplierGstin,
        financial_year: financialYear,
      },
    }),

  downloadGSTR9CSV: ({ supplierGstin, financialYear }) =>
    api.get('/reports/gstr9.csv', {
      params: {
        supplier_gstin: supplierGstin,
        financial_year: financialYear,
      },
      responseType: 'blob',
    }),

  /**
   * Phase 14k/14l — period-close lifecycle.
   *
   * ``getPeriodStatus`` returns {status: 'OPEN'|'LOCKED', locked_at,
   * locked_by, unlock_reason, notes} for a single period. Missing
   * rows on the server resolve to OPEN.
   */
  getPeriodStatus: ({ supplierGstin, financialYear, month }) =>
    api.get('/reports/period-status', {
      params: {
        supplier_gstin: supplierGstin,
        financial_year: financialYear,
        month,
      },
    }),

  /** Close (LOCK) a tax-ledger period. Typically called after
   *  filing GSTR-3B; subsequent ledger writes trigger 409. */
  closePeriod: ({ supplierGstin, financialYear, month, notes = null }) =>
    api.post('/reports/close-period', {
      supplier_gstin: supplierGstin,
      financial_year: financialYear,
      month,
      notes,
    }),

  /** Reopen a LOCKED period. SUPER_ADMIN only — router 403s
   *  everyone else. ``reason`` is mandatory. */
  reopenPeriod: ({ supplierGstin, financialYear, month, reason }) =>
    api.post('/reports/reopen-period', {
      supplier_gstin: supplierGstin,
      financial_year: financialYear,
      month,
      reason,
    }),

  // Billing stats
  getBillingStats: (params) => api.get('/billing/stats', { params }),
};

export default billingService;
